<!-- repeatable-group.component.html -->
<div *ngIf="rowsForm" [formGroup]="rowsForm">
  @if (isEditable) {
    <div class="repeatable-toolbar">
      <div class="left">
        <span class="title">{{ field?.label ?? '' }}</span>
        <span class="muted"> {{ currentCount }} de {{ maxRows }} </span>
      </div>

      <div class="center">
        <span class="muted">Rellene los campos necesarios</span>
      </div>

      <div class="right">
        <button
          pButton
          type="button"
          class="p-button-success"
          [label]="field?.input?.repeatableGroupConfiguration?.addButtonLabel ?? 'Agregar'"
          (click)="addRow()"
          [disabled]="!canAddRow()"
        ></button>

        @if (debug) {
          <button
            pButton
            type="button"
            class="p-button-secondary"
            icon="pi pi-info-circle"
            (click)="debugDump()"
            label="Debug"
          ></button>
        }
      </div>
    </div>
  }

  <div class="repeatable-group" formArrayName="rows" [style.--rg-cols]="visibleSubFields().length">
    <!-- Desktop header row -->
    <div class="repeatable-header">
      <div class="repeatable-header-cell"></div>

      <ng-container *ngFor="let sf of visibleSubFields(); trackBy: trackByIndex">
        <div class="repeatable-header-cell">
          <label class="field-label">{{ sf.label ?? sf.key }}</label>
        </div>
      </ng-container>

      <div class="repeatable-header-cell"></div>
    </div>

    <!-- Rows -->
    <div
      class="repeatable-row"
      *ngFor="let _row of rowsArray.controls; let rowIndex = index; trackBy: trackByIndex"
      [formGroupName]="rowIndex"
    >
      <!-- Desktop index -->
      <div class="repeatable-index repeatable-index-desktop">{{ rowIndex + 1 }}</div>

      <!-- Mobile header (only visible on mobile) -->
      <div class="repeatable-row-header repeatable-row-header-mobile">
        <div class="repeatable-index-mobile">{{ rowIndex + 1 }}</div>

        @if (canShowRowActions(rowIndex)) {
          <div class="repeatable-actions-mobile">
            @if (canShowProductInfo(rowIndex)) {
              <button
                pButton
                type="button"
                class="p-button-info p-button-rounded"
                icon="pi pi-info-circle"
                (click)="openProductInfoDialog(rowIndex)"
              ></button>
            }

            @if (isEditable) {
              <button
                pButton
                type="button"
                class="p-button-danger p-button-rounded"
                icon="pi pi-trash"
                (click)="deleteRow(rowIndex)"
                [disabled]="!canDeleteRow()"
              ></button>
            }
          </div>
        }
      </div>

      <!-- Cells -->
      <ng-container *ngFor="let sf of visibleSubFields(); trackBy: trackByIndex">
        <div class="repeatable-cell">
          <!-- Mobile label (hidden in desktop via CSS) -->
          <label class="field-label">{{ sf.label ?? sf.key }}</label>

          @switch (sf.input.type) {
            @case (INPUT.ONLY_TEXT) {
              <p class="read-only">{{ onlyTextCellText(sf, rowIndex) }}</p>
            }

            @case (INPUT.NUMERIC) {
              <input
                *ngIf="isEditable; else readOnly"
                pInputText
                type="number"
                [formControlName]="sf.key"
                placeholder="..."
                [readonly]="!!sf.computeValue"
                [ngClass]="getNumericInputClasses(sf, rowIndex)"
              />
            }

            @case (INPUT.TEXT) {
              <input
                *ngIf="isEditable; else readOnly"
                pInputText
                type="text"
                [formControlName]="sf.key"
                placeholder="..."
                [attr.maxlength]="sf.input.maxLength ?? null"
                [readonly]="!!sf.computeValue"
              />
            }

            @case (INPUT.PASSWORD) {
              <input
                *ngIf="isEditable; else readOnly"
                pInputText
                type="password"
                [formControlName]="sf.key"
                placeholder="..."
                [readonly]="!!sf.computeValue"
              />
            }

            @case (INPUT.TEXTAREA) {
              <textarea
                pTextarea
                class="w-100"
                [rows]="sf.input.textAreaConfiguration?.rows ?? 3"
                [cols]="sf.input.textAreaConfiguration?.cols ?? 20"
                [readonly]="!isEditable || !!sf.computeValue"
                [formControlName]="sf.key"
                placeholder="..."
              ></textarea>
            }

            @case (INPUT.TEXT_MASK) {
              <p-inputMask
                *ngIf="isEditable; else readOnly"
                [formControlName]="sf.key"
                [mask]="sf.input.textMaskConfiguration?.mask ?? ''"
                [placeholder]="sf.input.textMaskConfiguration?.mask ?? ''"
              ></p-inputMask>
            }

            @case (INPUT.DROPDOWN) {
              <p-select
                  *ngIf="isEditable; else readOnly"
                  [formControlName]="sf.key"
                  optionLabel="label"
                  optionValue="value"
                  [dataKey]="sf.input.dropdownConfiguration?.valuePrimaryKey ?? 'id'"
                  [options]="dropdownOptions(sf, rowIndex)"
                  [disabled]="!isEditable || !!sf.computeValue"
              ></p-select>
            }

            @case (INPUT.DROPDOWN_SEARCH) {
              <p-select
                  *ngIf="isEditable; else readOnly"
                  [formControlName]="sf.key"
                  [virtualScroll]="true"
                  [virtualScrollItemSize]="40"
                  [options]="dropdownOptions(sf, rowIndex)"
                  optionLabel="label"
                  optionValue="value"
                  [dataKey]="sf.input.dropdownConfiguration?.valuePrimaryKey ?? 'id'"
                  [filter]="true"
                  filterBy="label"
                  [disabled]="!isEditable || !!sf.computeValue"
              ></p-select>
            }

            @case (INPUT.MULTISELECT) {
              <p-listbox
                  *ngIf="isEditable; else readOnly"
                  [options]="dropdownOptions(sf, rowIndex)"
                  optionLabel="label"
                  optionValue="value"
                  [dataKey]="sf.input.dropdownConfiguration?.valuePrimaryKey ?? 'id'"
                  [metaKeySelection]="false"
                  [checkbox]="true"
                  [multiple]="true"
                  [showToggleAll]="false"
                  [listStyle]="{
                    'max-height': sf.input.dropdownConfiguration?.hasMaxHeight ? '100%' : '15rem',
                    'min-height': '18px'
                  }"
                  [formControlName]="sf.key"
                  [disabled]="!isEditable || !!sf.computeValue"
              ></p-listbox>
            }

            @case (INPUT.MULTISELECT_SEARCH) {
              <p-multiselect
                  *ngIf="isEditable; else readOnly"
                  [options]="dropdownOptions(sf, rowIndex)"
                  [formControlName]="sf.key"
                  optionLabel="label"
                  optionValue="value"
                  [dataKey]="sf.input.dropdownConfiguration?.valuePrimaryKey ?? 'id'"
                  [filter]="true"
                  filterBy="label"
                  [showToggleAll]="true"
                  placeholder="Seleccionar"
                  [maxSelectedLabels]="3"
                  styleClass="w-full md:w-80"
                  [disabled]="!isEditable || !!sf.computeValue"
              ></p-multiselect>
            }

            @case (INPUT.CALENDAR) {
              <p-datePicker
                *ngIf="isEditable; else readOnly"
                [formControlName]="sf.key"
                [firstDayOfWeek]="1"
                [dateFormat]="sf.input.calendarConfiguration?.calendarDateFormat ?? 'dd/mm/yy'"
                [disabledDates]="sf.input.calendarConfiguration?.disabledDates ?? []"
                (onSelect)="sf.input.calendarConfiguration?.onSelect?.($event)"
                dataType="string"
                placeholder="..."
                appendTo="body"
                [view]="sf.input.calendarConfiguration?.calendarView ?? 'date'"
                [disabled]="!isEditable || !!sf.computeValue"
              ></p-datePicker>
            }

            @case (INPUT.TIME) {
              <p-datePicker
                *ngIf="isEditable; else readOnly"
                [formControlName]="sf.key"
                [timeOnly]="true"
                placeholder="..."
                dateFormat="hh:mm"
                dataType="string"
                [disabled]="!isEditable || !!sf.computeValue"
              ></p-datePicker>
            }

            @case (INPUT.SWITCH) {
              @if (!isEditable) {
                <div style="pointer-events: none;">
                  <p-toggleswitch
                    [formControlName]="sf.key"
                    [trueValue]="'1'"
                    [falseValue]="'0'"
                  ></p-toggleswitch>
                </div>
              } @else {
                <p-toggleswitch
                  [formControlName]="sf.key"
                  [trueValue]="'1'"
                  [falseValue]="'0'"
                  [disabled]="!!sf.computeValue"
                ></p-toggleswitch>
              }
            }

            @default {
              <input
                *ngIf="isEditable; else readOnly"
                pInputText
                type="text"
                [formControlName]="sf.key"
                placeholder="..."
                [readonly]="!!sf.computeValue"
              />
            }
          }

          <ng-template #readOnly>
            <p class="read-only">{{ readOnlyCellText(sf, rowIndex) }}</p>
          </ng-template>
        </div>
      </ng-container>

      <!-- Desktop actions -->
      @if (canShowRowActions(rowIndex)) {
        <div class="repeatable-actions repeatable-actions-desktop">
          @if (canShowProductInfo(rowIndex)) {
            <button
              pButton
              type="button"
              class="p-button-info p-button-rounded"
              icon="pi pi-info-circle"
              (click)="openProductInfoDialog(rowIndex)"
            ></button>
          }

          @if (isEditable) {
            <button
              pButton
              type="button"
              class="p-button-danger p-button-rounded"
              icon="pi pi-trash"
              (click)="deleteRow(rowIndex)"
              [disabled]="!canDeleteRow()"
            ></button>
          }
        </div>
      }

      @if (shouldShowDoseRecommendation(rowIndex)) {
        <div class="repeatable-row-extra">
          <div class="dose-recommendation-card" [ngClass]="getDoseRecommendationStatusClass(rowIndex)">
            <div class="dose-recommendation-item dose-range">
              <span class="dose-recommendation-label">Dosis</span>
              <span class="dose-recommendation-value">{{ getDoseRecommendationText(rowIndex) }}</span>
            </div>

            @if (getDoseRecommendationStatusText(rowIndex) !== '') {
              <div class="dose-recommendation-item dose-status">
                <span class="dose-recommendation-label">Estado</span>
                <span class="dose-recommendation-value">{{ getDoseRecommendationStatusText(rowIndex) }}</span>
              </div>
            }

            @if (getPlazoSeguridadText(rowIndex) !== '') {
              <div class="dose-recommendation-item">
                <span class="dose-recommendation-label">Plazo seg.</span>
                <span class="dose-recommendation-value">{{ getPlazoSeguridadText(rowIndex) }}</span>
              </div>
            }

            @if (getIntervalosText(rowIndex) !== '') {
              <div class="dose-recommendation-item">
                <span class="dose-recommendation-label">Intervalos</span>
                <span class="dose-recommendation-value">{{ getIntervalosText(rowIndex) }}</span>
              </div>
            }

            @if (getMaxAplicacionesText(rowIndex) !== '') {
              <div class="dose-recommendation-item">
                <span class="dose-recommendation-label">N.º máx.</span>
                <span class="dose-recommendation-value">{{ getMaxAplicacionesText(rowIndex) }}</span>
              </div>
            }

            @if (getVolCaldoText(rowIndex) !== '') {
              <div class="dose-recommendation-item">
                <span class="dose-recommendation-label">Vol. caldo</span>
                <span class="dose-recommendation-value">{{ getVolCaldoText(rowIndex) }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
</div>


<p-dialog
  [(visible)]="infoDialogVisible"
  [modal]="true"
  [style]="{ width: '75vw', 'max-width': '1100px' }"
  [closable]="true"
  [dismissableMask]="true"
  [baseZIndex]="10000"
  [breakpoints]="{ '960px': '85vw', '640px': '95vw' }"
  [header]="getInfoDialogHeaderText()"
>
  <div class="repeatable-info-dialog">
    @if (isActiveInfoFertilizer()) {
      <table class="table table-striped table-bordered w-100 product-info-table">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Descripción</th>
            <th>Composición</th>
            <th>Ud. medida</th>
            <th>Envase</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogProductName() }}</td>
            <td>{{ getInfoDialogFertiDescripcionText() }}</td>
            <td>{{ getInfoDialogFertiComposicionText() }}</td>
            <td>{{ getInfoDialogFertiUnidadesText() }}</td>
            <td>{{ getInfoDialogFertiEnvaseText() }}</td>
          </tr>
        </tbody>
      </table>

      <table class="table table-striped table-bordered w-100 product-info-table product-fertilizer-table">
        <thead>
          <tr>
            <th>Tipo Abono Orgánico</th>
            <th>Abono con inhibidor</th>
            <th>N%</th>
            <th>P%</th>
            <th>K%</th>
            <th>Ca%</th>
            <th>S%</th>
            <th>Mg%</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogFertiTipoAbonoOrganicoText() }}</td>
            <td>{{ getInfoDialogFertiAbonoConInhibidorText() }}</td>
            <td>{{ getInfoDialogFertiFieldText('N') }}</td>
            <td>{{ getInfoDialogFertiFieldText('P') }}</td>
            <td>{{ getInfoDialogFertiFieldText('K') }}</td>
            <td>{{ getInfoDialogFertiFieldText('Ca') }}</td>
            <td>{{ getInfoDialogFertiFieldText('S') }}</td>
            <td>{{ getInfoDialogFertiFieldText('Mg') }}</td>
          </tr>
        </tbody>
      </table>

      <table class="table table-striped table-bordered w-100 product-info-table product-fertilizer-table product-fertilizer-table-wide">
        <thead>
          <tr>
            <th>Material Fertilizante</th>
            <th>N Total</th>
            <th>N Orgánico</th>
            <th>N Nítrico</th>
            <th>N Amoniacal</th>
            <th>N Ureico</th>
            <th>P2O5 Total</th>
            <th>P2O5 Agua</th>
            <th>P2O5 Citrato</th>
            <th>K20 Total</th>
            <th>Materia orgánica</th>
            <th>Calcio (CaO)</th>
            <th>Nombre del tipo</th>
            <th>Dosis</th>
            <th>Tratamiento recibido</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogFertiMaterialFertiText() }}</td>
            <td>{{ getInfoDialogFertiFieldText('n_total') }}</td>
            <td>{{ getInfoDialogFertiFieldText('n_organico') }}</td>
            <td>{{ getInfoDialogFertiFieldText('n_nitrico') }}</td>
            <td>{{ getInfoDialogFertiFieldText('n_amoniacal') }}</td>
            <td>{{ getInfoDialogFertiFieldText('n_ureico') }}</td>
            <td>{{ getInfoDialogFertiFieldText('p2o5_total') }}</td>
            <td>{{ getInfoDialogFertiFieldText('p2o5_agua') }}</td>
            <td>{{ getInfoDialogFertiFieldText('p2o5_amonico') }}</td>
            <td>{{ getInfoDialogFertiFieldText('k20_total') }}</td>
            <td>{{ getInfoDialogFertiFieldText('materia_organica') }}</td>
            <td>{{ getInfoDialogFertiFieldText('calcio') }}</td>
            <td>{{ getInfoDialogFertiFieldText('nombre_tipo') }}</td>
            <td>{{ getInfoDialogFertiFieldText('dosis') }}</td>
            <td>{{ getInfoDialogFertiFieldText('tratamiento_recibido') }}</td>
          </tr>
        </tbody>
      </table>

      <table class="table table-striped table-bordered w-100 product-info-table product-fertilizer-table">
        <thead>
          <tr>
            <th>Dosis aplicada</th>
            <th>Ud. medida</th>
            <th>Cadmio (Cd)</th>
            <th>Cobre (Cu)</th>
            <th>Plomo (Pb)</th>
            <th>Níquel (Ni)</th>
            <th>Zinc (Zn)</th>
            <th>Mercurio (Hg)</th>
            <th>Cromo total (Cr)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogFertiDosisAplicadaText() }}</td>
            <td>{{ getInfoDialogFertiUnidadAplicadaText() }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_cadmio') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_cobre') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_plomo') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_niquel') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_zinc') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_mercurio') }}</td>
            <td>{{ getInfoDialogFertiFieldText('m_cromo_total') }}</td>
          </tr>
        </tbody>
      </table>
    } @else {
      <table class="table table-striped table-bordered w-100 product-info-table">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Fecha de caducidad</th>
            <th>Materia activa</th>
            <th>Condiciones de uso</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogProductName() }}</td>
            <td>{{ getInfoDialogFechaCaducidadText() }}</td>
            <td>{{ getInfoDialogMateriaActivaText() }}</td>
            <td>{{ getInfoDialogCondicionesUsoText() }}</td>
          </tr>
        </tbody>
      </table>

      <table class="table table-striped table-bordered w-100 product-info-table product-treatment-table">
        <thead>
          <tr>
            <th>Uso</th>
            <th>Agente</th>
            <th>Dosis mínima</th>
            <th>Dosis máxima</th>
            <th>Dosis aplicada</th>
            <th>Ud. medida</th>
            <th>Vol. caldo</th>
            <th>Forma y época</th>
            <th>N.º máx.</th>
            <th>Intervalos</th>
            <th>Plazo seg.</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ getInfoDialogUsoText() }}</td>
            <td>{{ getInfoDialogAgenteText() }}</td>
            <td>{{ getInfoDialogDosisMinText() }}</td>
            <td>{{ getInfoDialogDosisMaxText() }}</td>
            <td>{{ getInfoDialogDosisAplicadaText() }}</td>
            <td>{{ getInfoDialogUnidadText() }}</td>
            <td>{{ getInfoDialogVolCaldoText() }}</td>
            <td>{{ getInfoDialogFormaEpocaText() }}</td>
            <td>{{ getInfoDialogMaxAplicacionesText() }}</td>
            <td>{{ getInfoDialogIntervalosText() }}</td>
            <td>{{ getInfoDialogPlazoSeguridadText() }}</td>
          </tr>
        </tbody>
      </table>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="product-info-actions">
      <p-button label="Cerrar" (click)="closeProductInfoDialog()"></p-button>
    </div>
  </ng-template>
</p-dialog>
